/**
 * Security Settings Tab - Session, password policy, invitation expiration
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import organizationService from '../../services/api/organizationService';
import type { OrgSettings } from '../../services/api/organizationService';

// ============================================================================
// Styled Components
// ============================================================================

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const ContentCard = styled(Card)``;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing[5]};
  padding-bottom: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const FormRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => theme.spacing[3]} 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  }
`;

const FieldInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const FieldLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldDescription = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const NumberInput = styled.input`
  width: 96px;
  height: 40px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    opacity: 1;
  }
`;

const ToggleTrack = styled.button<{ $on: boolean }>`
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: none;
  background: ${({ theme, $on }) =>
    $on ? theme.colors.primary.base : theme.colors.borders.medium};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.base};
  flex-shrink: 0;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const ToggleThumb = styled.span<{ $on: boolean }>`
  position: absolute;
  top: 3px;
  left: ${({ $on }) => ($on ? '22px' : '3px')};
  width: 18px;
  height: 18px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.text.white};
  transition: left ${({ theme }) => theme.transitions.base};
`;

const SaveRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const SuccessMsg = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.success.text};
`;

const ErrorMsg = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
`;

const SkeletonGroup = styled.div`
  margin-top: ${({ theme }) => theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SkeletonBar = styled.div<{ $width?: string }>`
  height: 14px;
  width: ${({ $width }) => $width ?? '100%'};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

// ============================================================================
// Toggle sub-component
// ============================================================================

interface ToggleSwitchProps {
  label: string;
  description?: string;
  value: boolean;
  onChange: (val: boolean) => void;
}

const ToggleSwitch = ({ label, description, value, onChange }: ToggleSwitchProps) => (
  <FormRow>
    <FieldInfo>
      <FieldLabel>{label}</FieldLabel>
      {description && <FieldDescription>{description}</FieldDescription>}
    </FieldInfo>
    <ToggleTrack
      type="button"
      $on={value}
      onClick={() => onChange(!value)}
      aria-pressed={value}
      aria-label={label}
    >
      <ToggleThumb $on={value} />
    </ToggleTrack>
  </FormRow>
);

// ============================================================================
// NumberField sub-component
// ============================================================================

interface NumberFieldProps {
  label: string;
  description?: string;
  value: number;
  min: number;
  max: number;
  onChange: (val: number) => void;
}

const NumberField = ({ label, description, value, min, max, onChange }: NumberFieldProps) => (
  <FormRow>
    <FieldInfo>
      <FieldLabel>{label}</FieldLabel>
      {description && <FieldDescription>{description}</FieldDescription>}
    </FieldInfo>
    <NumberInput
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={(e) => {
        const parsed = parseInt(e.target.value, 10);
        if (!isNaN(parsed)) {
          onChange(Math.min(max, Math.max(min, parsed)));
        }
      }}
    />
  </FormRow>
);

// ============================================================================
// Main Component
// ============================================================================

const DEFAULT_SETTINGS: OrgSettings = {
  sessionTimeoutMinutes: 30,
  passwordMinLength: 8,
  passwordRequireUppercase: false,
  passwordRequireNumber: false,
  passwordRequireSpecial: false,
  invitationExpirationDays: 14,
};

export const SecuritySettingsTab = () => {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<OrgSettings>(DEFAULT_SETTINGS);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['org-settings'],
    queryFn: () => organizationService.getOrgSettings(),
  });

  // Populate form when data arrives
  useEffect(() => {
    if (settings) {
      setForm(settings);
    }
  }, [settings]);

  const mutation = useMutation({
    mutationFn: (data: Partial<OrgSettings>) => organizationService.updateOrgSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-settings'] });
      setSaved(true);
      setSaveError(null);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: () => {
      setSaveError('Failed to save settings. Please try again.');
    },
  });

  const update = <K extends keyof OrgSettings>(key: K, value: OrgSettings[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
    setSaveError(null);
  };

  if (isLoading) {
    return (
      <Section>
        <ContentCard padding="lg">
          <SkeletonBar $width="200px" />
          <SkeletonGroup>
            <SkeletonBar />
            <SkeletonBar />
            <SkeletonBar $width="70%" />
          </SkeletonGroup>
        </ContentCard>
      </Section>
    );
  }

  return (
    <Section>
      {/* Session */}
      <ContentCard padding="lg">
        <CardTitle>Session</CardTitle>
        <NumberField
          label="Session Timeout"
          description="Minutes of inactivity before users are logged out (5–480)"
          value={form.sessionTimeoutMinutes}
          min={5}
          max={480}
          onChange={(v) => update('sessionTimeoutMinutes', v)}
        />
      </ContentCard>

      {/* Password Policy */}
      <ContentCard padding="lg">
        <CardTitle>Password Policy</CardTitle>
        <NumberField
          label="Minimum Password Length"
          description="Minimum number of characters required (6–128)"
          value={form.passwordMinLength}
          min={6}
          max={128}
          onChange={(v) => update('passwordMinLength', v)}
        />
        <ToggleSwitch
          label="Require Uppercase Letter"
          description="Passwords must contain at least one uppercase letter"
          value={form.passwordRequireUppercase}
          onChange={(v) => update('passwordRequireUppercase', v)}
        />
        <ToggleSwitch
          label="Require Number"
          description="Passwords must contain at least one digit"
          value={form.passwordRequireNumber}
          onChange={(v) => update('passwordRequireNumber', v)}
        />
        <ToggleSwitch
          label="Require Special Character"
          description="Passwords must contain at least one special character"
          value={form.passwordRequireSpecial}
          onChange={(v) => update('passwordRequireSpecial', v)}
        />
      </ContentCard>

      {/* Invitations */}
      <ContentCard padding="lg">
        <CardTitle>Invitations</CardTitle>
        <NumberField
          label="Invitation Expiration"
          description="Days before an invitation link expires (1–90)"
          value={form.invitationExpirationDays}
          min={1}
          max={90}
          onChange={(v) => update('invitationExpirationDays', v)}
        />
      </ContentCard>

      {/* Save */}
      <SaveRow>
        {saved && <SuccessMsg>Settings saved.</SuccessMsg>}
        {saveError && <ErrorMsg>{saveError}</ErrorMsg>}
        <Button
          variant="primary"
          onClick={() => mutation.mutate(form)}
          isLoading={mutation.isPending}
          disabled={mutation.isPending}
        >
          Save Settings
        </Button>
      </SaveRow>
    </Section>
  );
};

export default SecuritySettingsTab;
